import { config } from '@/config/env';
import type { CatalogSourceMeta } from '@/lib/api/analytics';
import { opiszDatyKarty } from '@/lib/dataKarty';
import type { KlasyfikacjaZrodla } from '@/components/SzufladaDowodu';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * POCHODZENIE DANYCH — jedno miejsce, w którym produkt twierdzi, SKĄD są liczby
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Dlaczego to nie może być wpisane w ekranie: klasyfikacja źródła jest TWIERDZENIEM O ŚWIECIE,
 * a nie etykietą wizualną. Zdanie „zbiór kontrolowany: dane wprowadzone do weryfikacji" wpisane
 * na sztywno w ekranie mówi to samo także wtedy, gdy Control Center zostanie podłączony do
 * produkcji — i wtedy jest po prostu nieprawdziwe. Właściciel czytający „dane wprowadzone do
 * weryfikacji" nad prawdziwym wolumenem sprzedaży podejmie decyzję na fałszywym założeniu.
 *
 * Odwrotny błąd jest groźniejszy: gdyby ekran twierdził „produkcja" tam, gdzie stoi zbiór
 * testowy, ktoś potraktowałby liczby testowe jak realne wyniki firmy.
 *
 * Dlatego klasyfikacja WYNIKA z konfiguracji środowiska, a nie z tego, co ktoś napisał w JSX.
 */

/**
 * Skąd pochodzą dane systemu kierowców w BIEŻĄCYM środowisku.
 * Na produkcji to realne dostawy; poza nią — zbiór kontrolowany.
 */
export function klasyfikacjaDrive(): KlasyfikacjaZrodla {
  return config.dataOrigin === 'production' ? 'PRODUKCJA' : 'TEST_KONTROLOWANE';
}

/**
 * Świeżość danych Drive. Poza produkcją mówimy wprost, że to środowisko testowe — bo „bieżąca"
 * bez tego dopisku brzmi jak zapewnienie o aktualności danych FIRMOWYCH.
 */
export function swiezoscDrive(): string {
  return config.dataOrigin === 'production'
    ? 'bieżąca'
    : 'bieżąca dla środowiska TEST (zbiór kontrolowany)';
}

/**
 * Sprzedaż z CRM. Świeżość transakcji jest NIEUSTALONA niezależnie od środowiska — źródło nie
 * raportuje momentu odczytu i nie wolno tego zamaskować ładniejszym słowem. To jest największa
 * otwarta luka programu i produkt ma ją powtarzać przy każdej liczbie, która z niej korzysta.
 */
export function klasyfikacjaSprzedazy(): KlasyfikacjaZrodla {
  return 'SWIEZOSC_NIEUSTALONA';
}

export function swiezoscSprzedazy(): string {
  return 'NIEUSTALONA — źródło nie raportuje momentu odczytu';
}

/** Świeżość dla liczby łączącej oba systemy — każda strona opisana osobno, bo różnią się. */
export function swiezoscMiedzysystemowa(): string {
  return `strona CRM: ${swiezoscSprzedazy()} · strona Drive: ${swiezoscDrive()}`;
}

/**
 * Klasyfikacja KATALOGU DAŃ — wyprowadzana z proweniencji, nie deklarowana.
 *
 * `TEST_REALNE_REFERENCYJNE` wolno powiedzieć wyłącznie wtedy, gdy odczyt zostawił ślad: znane
 * źródło ORAZ data karty. Bez któregokolwiek z tych dwóch zostaje „świeżość nieustalona" — bo
 * katalog, o którym nie wiemy, z którego dnia pochodzi, niczym nie różni się od kopii sprzed roku.
 *
 * Rozdzielenie od sprzedaży jest tu celowe i twarde: to, że OFERTA jest świeża, nie mówi nic
 * o świeżości TRANSAKCJI. Mieszanie tych dwóch statusów byłoby najprostszym sposobem na ciche
 * zamknięcie otwartej luki programu.
 */
export function klasyfikacjaKatalogu(meta: CatalogSourceMeta | null | undefined): KlasyfikacjaZrodla {
  if (!meta?.zrodlo) return 'SWIEZOSC_NIEUSTALONA';
  const daty = Array.isArray(meta.data_karty) ? meta.data_karty : [];
  if (daty.length === 0) return 'SWIEZOSC_NIEUSTALONA';
  return config.dataOrigin === 'production' ? 'PRODUKCJA' : 'TEST_REALNE_REFERENCYJNE';
}

/** Świeżość katalogu: data KARTY (stan oferty) i moment ODCZYTU — to dwie różne rzeczy. */
export function swiezoscKatalogu(
  meta: CatalogSourceMeta | null | undefined,
  odczytanoISO: string | null | undefined,
): string {
  if (!meta) return 'NIEUSTALONA — brak zapisanej proweniencji odczytu';
  const karta = opiszDatyKarty(meta.data_karty);
  const odczyt = odczytanoISO ? new Date(odczytanoISO).toISOString().slice(0, 16).replace('T', ' ') : 'nieznany';
  return `karta z ${karta} · odczytana ${odczyt} UTC`;
}
